<script setup>
import { ref, reactive, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { gsap } from 'gsap'

const props = defineProps({
    active: {
        type: Boolean,
        default: false
    },
    label: {
        type: String,
        required: true
    },
    link: {
        type: String,
        required: false
    },
    obfuscated: {
        type: Boolean,
        default: false
    },
    bigLight: {
        type: Boolean,
        default: false
    },
    type: {
        type: String,
        default: null
    },
    target: {
        type: String,
        default: '_blank'
    }
})

const router = useRouter()
const loaded = ref(false)
const timeline = gsap.timeline()

const mousePosition = reactive({ x: 0, y: 0 })
const buttonPosition = reactive({ x: 0, y: 0 })
const buttonSize = reactive({ width: 0, height: 0 })
const fadeIntensity = reactive({ left: 0, right: 0, top: 0, bottom: 0 })

const buttonRef = ref(null)
const lightRef = ref(null)
const obfuscatedRef = ref(null)

function obfuscateLink(link) {
    if (obfuscatedRef.value) {
        obfuscatedRef.value.addEventListener('click', () => {
            router.push({ path: link })
        })
    }
}

function lightOn(event, button, light) {
    mousePosition.x = event.clientX
    mousePosition.y = event.clientY
    enterAnimationEase(event, light)
}

function lightMove(event, button, light) {
    const rect = button.getBoundingClientRect()
    buttonPosition.x = rect.left
    buttonPosition.y = rect.top

    let x = mousePosition.x - buttonPosition.x
    let y = mousePosition.y - buttonPosition.y
    button.style.setProperty('--mouse-x', `${x}px`)
    button.style.setProperty('--mouse-y', `${y}px`)
}

function lightOff(light) {
    timeline.to(light, {
        opacity: 0,
        duration: 0.5,
        ease: 'power3.Out'
    })
}


function enterAnimationEase(event, light) {
    mousePosition.x = event.clientX
    const rect = buttonRef.value.getBoundingClientRect()
    buttonPosition.x = rect.left
    buttonPosition.y = rect.top
    let originPosition = {
        x: mousePosition.x - buttonPosition.x,
    }
    let opositePosition = getOpositePosition(light)
    let offset = {
        x: (originPosition.x > opositePosition.x) ? ( buttonSize.width / 3 * 2  ): buttonSize.width/3,
    }

    timeline.clear()
    timeline.fromTo(
        light,
        {
            left: `${mousePosition.x - buttonPosition.x + ( buttonSize.width / 2 )}px`,
            opacity: 0
        },
        {
            left: `${opositePosition.x + offset.x}px`,
            opacity: 0.75,
            duration: 1,
            ease: 'power2.out'
        }
    )
}

function getOpositePosition(light) {
    let x = mousePosition.x - buttonPosition.x
    let xPercent = Math.round((x / buttonSize.width) * 100)
    let xDistance = buttonSize.width 
    
    let y = mousePosition.y - buttonPosition.y
    let yPercent = Math.round((y / buttonSize.height) * 100)
    let yDistance = buttonSize.height / 2 + buttonSize.height / 4
    
    return {
        x: buttonSize.width - Math.round((xDistance * xPercent) / 100),
        y: buttonSize.height - Math.round((yDistance / 2) * yPercent / 100) - yDistance / 4
    }
}

function limitMinMax(value, min, max) {
    return Math.min(Math.max(value, min), max)
}

function getSmallestIntensity() {
    return Math.min(fadeIntensity.left, fadeIntensity.right, fadeIntensity.top, fadeIntensity.bottom)
}

onMounted(() => {
    loaded.value = true
    if (props.obfuscated) {
        obfuscateLink(props.link)
    }
    const button = buttonRef.value
    const light = lightRef.value
    if (!button || !light) return

    const rect = button.getBoundingClientRect()
    buttonSize.width = rect.width
    buttonSize.height = rect.height
    buttonPosition.x = rect.left
    buttonPosition.y = rect.top
})

onUnmounted(() => {

    timeline.clear()
    timeline.kill()
})
</script>


<template>
    <div
        class="illuminated-button"
        @mouseenter="lightOn($event, buttonRef, lightRef)"
        @mousemove="lightMove($event, buttonRef, lightRef)"
        @mouseleave="lightOff(lightRef)"
        style="--mouse-x: 0px; --mouse-y: 0px;"
        ref="buttonRef"
    >
        <button v-if="['button', 'submit'].includes(props.type)" 
            :type="props.type"
            @click="props.obfuscated ? null : router.push(props.link)"
            class="label">
                {{ props.label }}
        </button>
        <a :href="props.link" :target="props.target === 'default' ? '_self' : props.target" rel="noopener" v-else-if="props.link && !props.obfuscated">
            <div class="label" :class="{ active: props.active }">
                {{ props.label }}
            </div>
        </a>
        <router-link :to="props.link" v-else-if="!props.obfuscated">
            <div class="label" :class="{ active: props.active }">
                {{ props.label }}
            </div>
        </router-link>
        <div v-else :class="{ active: props.active }" class="obfuscated label" ref="obfuscatedRef">
            {{ props.label }}
        </div>
        <div class="light" 
            :class="{ 'light--big': props.bigLight }"
            :data-intensity="getSmallestIntensity()" 
            ref="lightRef">
        </div>
    </div>
</template>


<style lang="scss" scoped>


.illuminated-button{
    color: #ffffff;
    border-radius: 25px;
    position: relative;
    width: fit-content;
    transition : box-shadow 1s ease-out;
    width: 100%;
    color: #FFF;
    text-align: center;
    font-family: $secondary-font-family;
    font-size: 13px;
    font-weight: 600;
    text-transform: uppercase;

    & button{
        color: #FFF;
        text-align: center;
        font-family: $secondary-font-family;
        font-size: 13px;
        font-weight: 600;
        text-transform: uppercase;
        background-color: transparent;
        border: none;

    }

    &::before{
        content: "";
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        height: 100%;
        background: transparent linear-gradient(272deg, #47BCFF -9.85%, #134898 94.36%) 0% 0% no-repeat padding-box;
        border-radius: 25px;
        z-index: -2;
    }

    & > *:not(.light){
        position: relative;
        z-index: 1;
    }

    & .light{
        position: absolute;
        z-index: -1;
        height: 89px;  /*  nice */
        width: 169px;
        top: calc(47px / 2);
        left: 70px;
        background-color: #02ABFF;
        border-radius: 50%;
        filter: blur(20px);
        opacity: 0;
        transform: translate(-100%, -50%);
        filter: blur(50px);
        pointer-events: none;

        &--big{
            width: 169px;
            height: 169px;
            left: calc(50% - 84.5px);
            top: calc(47px / 2);
        }
        
    }

    & .label,
    & button{
        padding: 8px 25px;
        min-height: 46px;
        width: 100%;
        border-radius: 50px;
        color: $white;
    }

    &.demo-button .label{
        padding: 8px 40px;
    }

    @media (min-width: 767px) {
        width: unset;

        & .label,
        &.demo-button .label,
        & button{
            padding: 8px 50px;
        }
    }
}



</style>